import React, { useState, useEffect } from "react";
import PulseLoader from "@/components/PulseLoader";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import authService from "../../../utils/authService";
import NotebookStudentPreview from "./NotebookStudentPreview";
import { cellsFromNotebookSource } from "../../../utils/notebookPreview";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FaLock, FaGripVertical } from 'react-icons/fa';

interface Course {
  course_id: string;
  course_name: string;
  subjects: Subject[];
  level: string;
  Existing_Subjects: string[];
}

interface Subject {
  subject_id: string;
  subject_name: string;
}

interface Topic {
  topic_id: string;
  topic_name: string;
}

interface SubTopic {
  sub_topic_id: string;
  sub_topic_name: string;
  notes: number;
  videos: number;
  mcq: number;
  coding: number;
  notebooks?: number;
}

interface ContentItem {
  content_id: number;
  text: string;
  time: string;
  level: string;
  type?: string;
  content?:string
  playback_info?:string
  otp?:string
}
interface Structure {
  level: string;
  score: number;
  testcase?: string;
  time: number;
}

interface Data {
  mcq: Structure[];
  coding: Structure[];
}


interface ContentData {
  videos: { [key: string]: ContentItem };
  files: { [key: string]: ContentItem };
  notebooks: { [key: string]: ContentItem };
}

interface CountData {
  mcq: { [key: string]: number };
  coding: { [key: string]: number };
}

interface SelectedContentItem {
  content_id: number;
  time: string;
}

interface SelectedItemsData {
  [key: string]: {
    videos: { [key: string]: SelectedContentItem };
    files: { [key: string]: SelectedContentItem };
    notebooks: { [key: string]: SelectedContentItem };
    topic: string;
    topicId: string;
    subtopicName: string;
    mcq: { [key: string]: number };
    coding: { [key: string]: number };
    optionalMcq: { [key: string]: number };
    optionalCoding: { [key: string]: number };
  };
}
type McqCodingType = 'mcq' | 'coding' | 'optionalMcq' | 'optionalCoding';

interface SelectedMcqCoding {
  [key: string]: {
    mcq: boolean;
    coding: boolean;
    optionalMcq: boolean;
    optionalCoding: boolean;
  };
}

interface InputCounts {
  [key: string]: {
    mcq: { [key: string]: number };
    coding: { [key: string]: number };
    optionalMcq: { [key: string]: number };
    optionalCoding: { [key: string]: number };
  };
}

interface ExistingDataTopic {
  id: string;
  name: string;
  subtopics: {
    id: string;
    subtopicName: string;
    mcq: { [key: string]: number };
    coding: { [key: string]: number };
    optionalMcq?: { [key: string]: number };
    optionalCoding?: { [key: string]: number };
    data: ContentItem[];
  }[];
}

const CreateSubjectPlan: React.FC = () => {
  const navigate = useNavigate();
  const [timeData, setTimeData] = useState<Data | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<Subject>({ subject_id: "", subject_name: "" });
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [existSubjects, setExistSubjects] = useState<String[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course>({ course_id: "", course_name: "", subjects: [],level:"",Existing_Subjects:[] });
  const [subjectPlan, setSubjectPlan] = useState("");
  const [topicLevels, setTopicLevels] = useState<string[]>(['level1', 'level2', 'level3']);
  const [selectedTopic, setSelectedTopic] = useState<Topic>({ topic_id: "", topic_name: "" });
  const [selectedsub, setSelectedsub] = useState<SubTopic>({ sub_topic_id: "", sub_topic_name: "", notes: 0, videos: 0, mcq: 0, coding: 0 });
  const [showModal, setShowModal] = useState(false);
  const [notebookPreviewModal, setNotebookPreviewModal] = useState(false);
  const [subTopicData, setSubTopicData] = useState<SubTopic[]>([]);
  const [contentData, setContentData] = useState<ContentData>({ videos: {}, files: {}, notebooks: {} });
  const [inputCounts, setInputCounts] = useState<InputCounts>({});
  const [selectedItemsData, setSelectedItemsData] = useState<SelectedItemsData>({});
  const [selectedMcqCoding, setSelectedMcqCoding] = useState<SelectedMcqCoding>({});
  const [time, setTime] = useState<number>(0);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [count, setCount] = useState<CountData>({ mcq: {}, coding: {} });
  const [existingData, setExistingData] = useState<ExistingDataTopic[]>([]);
  const [restrictedSubtopicIds, setRestrictedSubtopicIds] = useState<string[]>([]);
  const [restrictedTopicIds, setRestrictedTopicIds] = useState<string[]>([]);
  const [reorderedTopics, setReorderedTopics] = useState<Topic[]>([]);
  const [reorderedSubTopics, setReorderedSubTopics] = useState<{ [topicId: string]: SubTopic[] }>({});
  const [dragError, setDragError] = useState<string | null>(null);

  // Loading states
  const [fetchContentLoading, setFetchContentLoading] = useState(false);
  const [fetchCountLoading, setFetchCountLoading] = useState(false);
  const [fetchSubTopicLoading, setFetchSubTopicLoading] = useState(false);
  const [fetchSubjectsLoading, setFetchSubjectsLoading] = useState(false);
  const [fetchCourseLoading, setFetchCourseLoading] = useState(false);
  const [fetchTopicLoading, setFetchTopicLoading] = useState(false);
  const [fetchRestrictedLoading, setFetchRestrictedLoading] = useState(false);

  useEffect(() => {
    const calculateTime = () => {
      let calculatedTime = 0;
      Object.keys(selectedItemsData).forEach(key => {
        const data = selectedItemsData[key];
        // Saved + API keys can briefly coexist for the same content_id; count each once per subtopic
        const countedContentIds = new Set<string>();

        if (data.files) {
          Object.values(data.files).forEach((file) => {
            const contentId = file.content_id?.toString();
            if (contentId) {
              if (countedContentIds.has(contentId)) return;
              countedContentIds.add(contentId);
            }
            calculatedTime += parseInt(file.time, 10) || 0;
          });
        }
        if (data.videos) {
          Object.values(data.videos).forEach((video) => {
            const contentId = video.content_id?.toString();
            if (contentId) {
              if (countedContentIds.has(contentId)) return;
              countedContentIds.add(contentId);
            }
            calculatedTime += parseInt(video.time, 10) || 0;
          });
        }
        if (data.notebooks) {
          Object.values(data.notebooks).forEach((notebook) => {
            const contentId = notebook.content_id?.toString();
            if (contentId) {
              if (countedContentIds.has(contentId)) return;
              countedContentIds.add(contentId);
            }
            calculatedTime += parseInt(notebook.time, 10) || 0;
          });
        }
        if (data.mcq) {
          Object.keys(data.mcq).forEach(level => {
            const mcqLevel = level.replace('level', 'Level');
            const mcqTimeData = timeData?.mcq.find(item => item.level === mcqLevel);
            if (mcqTimeData) {
              calculatedTime += data.mcq[level] * mcqTimeData.time;
            }
          });
        }
        if (data.coding) {
          Object.keys(data.coding).forEach(level => {
            const codingLevel = level.replace('level', 'Level');
            const codingTimeData = timeData?.coding.find(item => item.level === codingLevel);
            if (codingTimeData) {
              calculatedTime += data.coding[level] * codingTimeData.time;
            }
          });
        }
        // Optional MCQ and coding don't contribute to time calculation
      });
      setTime(calculatedTime);
    };
    calculateTime();
  }, [selectedItemsData, timeData]);
  useEffect(() => {
    if (existingData.length > 0) {
      const initialSelectedItemsData: SelectedItemsData = {};
      const initialInputCounts: InputCounts = {};

      existingData.forEach(topic => {
        topic.subtopics.forEach(subtopic => {
          const videos: { [key: string]: SelectedContentItem } = {};
          const files: { [key: string]: SelectedContentItem } = {};
          const notebooks: { [key: string]: SelectedContentItem } = {};
          subtopic.data.forEach(item => {
            if (item.content_id) {
              if (item.type === 'video') {
                // API load uses label keys; saved plan uses content_id — re-keyed when subtopic opens
                videos[item.content_id.toString()] = { content_id: item.content_id, time: item.time };
              } else if (item.type === 'file') {
                files[item.content_id.toString()] = { content_id: item.content_id, time: item.time };
              } else if (item.type === 'notebook' || item.type === 'marimo') {
                notebooks[item.content_id.toString()] = { content_id: item.content_id, time: item.time || '0' };
              }
            }
          });
          initialSelectedItemsData[subtopic.id] = {
            videos: videos,
            files: files,
            notebooks: notebooks,
            topic: topic.name,
            topicId: topic.id,
            subtopicName: subtopic.subtopicName,
            mcq: subtopic.mcq || {},
            coding: subtopic.coding || {},
            optionalMcq: subtopic.optionalMcq || {},
            optionalCoding: subtopic.optionalCoding || {}
          };
          initialInputCounts[subtopic.id] = {
            mcq: subtopic.mcq || {},
            coding: subtopic.coding || {},
            optionalMcq: subtopic.optionalMcq || {},
            optionalCoding: subtopic.optionalCoding || {}
          };
        });
      });
      setSelectedItemsData(initialSelectedItemsData);
      setInputCounts(initialInputCounts);
      setSelectedMcqCoding(prev => {
        const newSelectedMcqCoding: SelectedMcqCoding = { ...prev };
        existingData.forEach(topic => {
          topic.subtopics.forEach(subtopic => {
            newSelectedMcqCoding[subtopic.id] = {
              mcq: Object.values(subtopic.mcq || {}).some(count => count > 0),
              coding: Object.values(subtopic.coding || {}).some(count => count > 0),
              optionalMcq: Object.values(subtopic.optionalMcq || {}).some(count => count > 0),
              optionalCoding: Object.values(subtopic.optionalCoding || {}).some(count => count > 0)
            };
          });
        });
        return newSelectedMcqCoding;
      });
    }
  }, [existingData]);

  // Re-key saved content (stored by content_id) to API keys when subtopic content loads.
  // Do not add a second entry — that was doubling total time on expand after refresh.
  useEffect(() => {
    const currentSubtopicId = selectedsub.sub_topic_id;
    if (!currentSubtopicId) return;

    setSelectedItemsData(prev => {
      const currentSelectedData = prev[currentSubtopicId];
      if (!currentSelectedData) return prev;

      const rekeyItems = (
        apiItems: { [key: string]: ContentItem },
        selectedItems: { [key: string]: SelectedContentItem }
      ) => {
        const updated = { ...selectedItems };
        let changed = false;

        Object.entries(apiItems).forEach(([apiKey, item]) => {
          if (updated[apiKey]) return;

          const existingKey = Object.keys(updated).find(
            (key) => updated[key].content_id === item.content_id
          );

          // Move existing selection to the API key; preserve saved time over API default
          if (existingKey && existingKey !== apiKey) {
            updated[apiKey] = {
              content_id: item.content_id,
              time: updated[existingKey].time ?? item.time,
            };
            delete updated[existingKey];
            changed = true;
          }
        });

        return { items: updated, changed };
      };

      let videosChanged = false;
      let filesChanged = false;
      let notebooksChanged = false;
      let videos = currentSelectedData.videos || {};
      let files = currentSelectedData.files || {};
      let notebooks = currentSelectedData.notebooks || {};

      if (contentData.videos && Object.keys(contentData.videos).length > 0) {
        const result = rekeyItems(contentData.videos, videos);
        videos = result.items;
        videosChanged = result.changed;
      }

      if (contentData.files && Object.keys(contentData.files).length > 0) {
        const result = rekeyItems(contentData.files, files);
        files = result.items;
        filesChanged = result.changed;
      }

      if (contentData.notebooks && Object.keys(contentData.notebooks).length > 0) {
        const result = rekeyItems(contentData.notebooks, notebooks);
        notebooks = result.items;
        notebooksChanged = result.changed;
      }

      if (!videosChanged && !filesChanged && !notebooksChanged) return prev;

      return {
        ...prev,
        [currentSubtopicId]: {
          ...currentSelectedData,
          videos,
          files,
          notebooks,
        },
      };
    });
  }, [contentData, selectedsub.sub_topic_id]);

  const handleCheckboxChange = (
    item: string,
    type: "mcq" | "coding" | "optionalMcq" | "optionalCoding" | "video" | "file" | "notebook",
    subTopic: SubTopic,
    itemData?: ContentItem,
    checked?: boolean
  ) => {
    // Prevent changes for restricted subtopics
    if (isContentSelectionDisabled(subTopic.sub_topic_id)) {
      return;
    }

    setSelectedItemsData(prev => {
      const newData = { ...prev };
  
      if (!newData[subTopic.sub_topic_id]) {
        newData[subTopic.sub_topic_id] = {
          videos: {} as { [key: string]: SelectedContentItem },
          files: {} as { [key: string]: SelectedContentItem },
          notebooks: {} as { [key: string]: SelectedContentItem },
          topic: selectedTopic.topic_name || "",
          topicId: selectedTopic.topic_id || "",
          subtopicName: subTopic.sub_topic_name,
          mcq: {},
          coding: {},
          optionalMcq: {},
          optionalCoding: {}
        };
      }
  
      if (type === "mcq" || type === "coding" || type === "optionalMcq" || type === "optionalCoding") {
        if (checked) {
          if (type === "optionalMcq" || type === "optionalCoding") {
            // For optional content, start with empty counts
            newData[subTopic.sub_topic_id][type] = {};
          } else {
            newData[subTopic.sub_topic_id][type] = calculateMaxCounts(subTopic)[type];
          }
        } else {
          newData[subTopic.sub_topic_id][type] = {};
        }
      } else {
        const collection =
          type === "video" ? "videos" : type === "notebook" ? "notebooks" : "files";

        if (checked && itemData) {
          newData[subTopic.sub_topic_id][collection][item] = {
            content_id: itemData.content_id,
            time: itemData.time
          };
        } else {
          const { [item]: removed, ...rest } = newData[subTopic.sub_topic_id][collection];
          newData[subTopic.sub_topic_id][collection] = rest;
        }
      }
  
      // Clean up if the subtopic has no data
      if (
        Object.keys(newData[subTopic.sub_topic_id].videos).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].files).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].notebooks || {}).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].mcq).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].coding).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].optionalMcq).length === 0 &&
        Object.keys(newData[subTopic.sub_topic_id].optionalCoding).length === 0
      ) {
        delete newData[subTopic.sub_topic_id];
      }
  
      return newData;
    });
  
    // ✅ Fix for mcq/coding type enforcement
    if (type === "mcq" || type === "coding" || type === "optionalMcq" || type === "optionalCoding") {
      setSelectedMcqCoding(prev => {
        const updated = { ...prev };
  
        if (!updated[subTopic.sub_topic_id]) {
          updated[subTopic.sub_topic_id] = { mcq: false, coding: false, optionalMcq: false, optionalCoding: false };
        }
  
        updated[subTopic.sub_topic_id][type] = !!checked;
  
        // Clean up if all are false
        if (
          !updated[subTopic.sub_topic_id].mcq &&
          !updated[subTopic.sub_topic_id].coding &&
          !updated[subTopic.sub_topic_id].optionalMcq &&
          !updated[subTopic.sub_topic_id].optionalCoding
        ) {
          delete updated[subTopic.sub_topic_id];
        }
  
        return updated;
      });
  
      setInputCounts(prev => {
        const updated = { ...prev };
        if (checked) {
          if (type === "optionalMcq" || type === "optionalCoding") {
            updated[subTopic.sub_topic_id] = {
              ...updated[subTopic.sub_topic_id],
              [type]: {}
            };
          } else {
            updated[subTopic.sub_topic_id] = {
              ...updated[subTopic.sub_topic_id],
              [type]: calculateMaxCounts(subTopic)[type]
            };
          }
        } else {
          updated[subTopic.sub_topic_id] = {
            ...updated[subTopic.sub_topic_id],
            [type]: {}
          };
        }
        return updated;
      });
    }
  };
  
  const toggleSubTopicContent = (subTopic: SubTopic) => {
    setSelectedsub((prev) => {
      const newSelectedSub = prev.sub_topic_id === subTopic.sub_topic_id
        ? { sub_topic_id: "", sub_topic_name: "", notes: 0, videos: 0, mcq: 0, coding: 0, notebooks: 0 }
        : { sub_topic_id: subTopic.sub_topic_id, sub_topic_name: subTopic.sub_topic_name, notes: subTopic.notes, videos: subTopic.videos, mcq: subTopic.mcq, coding: subTopic.coding, notebooks: subTopic.notebooks || 0 };
  
      return newSelectedSub;
    });
  
    // Initialize counts to 0 when a subtopic is selected
    setInputCounts((prevCounts) => {
      if (!prevCounts[subTopic.sub_topic_id]) {
        return {
          ...prevCounts,
          [subTopic.sub_topic_id]: {
            mcq: {},
            coding: {},
            optionalMcq: {},
            optionalCoding: {}
          }
        };
      }
      return prevCounts;
    });
  };
  
  const calculateMaxCounts = (subTopic: SubTopic) => {
    const maxMcqCounts = calculateMaxMcqCount(subTopic);
    const maxCodingCounts = calculateMaxCodingCount(subTopic);
    return { mcq: maxMcqCounts, coding: maxCodingCounts };
  };

  // Helper function to validate counts
  const validateCounts = (subTopicId: string, type: McqCodingType, level: string, value: number) => {
    const currentMcqCount = inputCounts[subTopicId]?.mcq?.[level] || 0;
    const currentCodingCount = inputCounts[subTopicId]?.coding?.[level] || 0;
    const currentOptionalMcqCount = inputCounts[subTopicId]?.optionalMcq?.[level] || 0;
    const currentOptionalCodingCount = inputCounts[subTopicId]?.optionalCoding?.[level] || 0;
    
    const totalMcq = (type === 'mcq' || type === 'optionalMcq') ? (type === 'mcq' ? value : currentMcqCount) : currentMcqCount;
    const totalOptionalMcq = (type === 'mcq' || type === 'optionalMcq') ? (type === 'optionalMcq' ? value : currentOptionalMcqCount) : currentOptionalMcqCount;
    const totalCoding = (type === 'coding' || type === 'optionalCoding') ? (type === 'coding' ? value : currentCodingCount) : currentCodingCount;
    const totalOptionalCoding = (type === 'coding' || type === 'optionalCoding') ? (type === 'optionalCoding' ? value : currentOptionalCodingCount) : currentOptionalCodingCount;
    
    const maxMcq = count?.mcq?.[level] || 0;
    const maxCoding = count?.coding?.[level] || 0;
    
    // Validate that total doesn't exceed available count
    if (type === 'mcq' || type === 'optionalMcq') {
      return totalMcq + totalOptionalMcq <= maxMcq;
    } else if (type === 'coding' || type === 'optionalCoding') {
      return totalCoding + totalOptionalCoding <= maxCoding;
    }
    return true;
  };
  
  

  const isAllSelected = (
    subTopicId: string,
    videos?: { [key: string]: ContentItem },
    files?: { [key: string]: ContentItem },
    notebooks?: { [key: string]: ContentItem }
  ) => {
    const currentSubTopicData = selectedItemsData[subTopicId] || { videos: {}, files: {}, notebooks: {} };
    const currentMcqCoding = selectedMcqCoding[subTopicId] || { mcq: false, coding: false, optionalMcq: false, optionalCoding: false };
    
    // Check available videos and files
    const availableVideos = Object.entries(videos || {}).filter(([_, video]) =>
      topicLevels.includes(video.level) || topicLevels.length === 0
    );
    const availableFiles = Object.entries(files || {}).filter(([_, file]) =>
      topicLevels.includes(file.level) || topicLevels.length === 0
    );
    const availableNotebooks = Object.keys(notebooks || {});
    const selectedVideosCount = Object.entries(currentSubTopicData.videos || {}).filter(([videoKey, video]) => {
      const originalVideo = contentData.videos[videoKey];
      return originalVideo && (topicLevels.includes(originalVideo.level) || topicLevels.length === 0);
    }).length;
    const selectedFilesCount = Object.entries(currentSubTopicData.files || {}).filter(([fileKey, file]) => {
      const originalFile = contentData.files[fileKey];
      return originalFile && (topicLevels.includes(originalFile.level) || topicLevels.length === 0);
    }).length;
    const selectedNotebooksCount = Object.keys(currentSubTopicData.notebooks || {}).length;

    // Check available MCQ and coding content
    const availableMcqLevels = topicLevels.filter(level => count?.mcq?.[level] && count.mcq[level] > 0);
    const availableCodingLevels = topicLevels.filter(level => count?.coding?.[level] && count.coding[level] > 0);
    
    // If no content is available at all, return false
    if (
      availableVideos.length === 0 &&
      availableFiles.length === 0 &&
      availableNotebooks.length === 0 &&
      availableMcqLevels.length === 0 &&
      availableCodingLevels.length === 0
    ) {
      return false;
    }

    const hasAllVideos = availableVideos.length === 0 || selectedVideosCount === availableVideos.length;
    const hasAllFiles = availableFiles.length === 0 || selectedFilesCount === availableFiles.length;
    const hasAllNotebooks =
      availableNotebooks.length === 0 || selectedNotebooksCount === availableNotebooks.length;
    const hasAllMcq = availableMcqLevels.length === 0 || currentMcqCoding.mcq;
    const hasAllCoding = availableCodingLevels.length === 0 || currentMcqCoding.coding;

    return hasAllVideos && hasAllFiles && hasAllNotebooks && hasAllMcq && hasAllCoding;
  };

  const handleSelectAllChange = (
    subTopic: SubTopic,
    videos?: { [key: string]: ContentItem },
    files?: { [key: string]: ContentItem },
    notebooks?: { [key: string]: ContentItem }
  ) => {
    // Prevent changes for restricted subtopics
    if (isContentSelectionDisabled(subTopic.sub_topic_id)) {
      return;
    }

    const isCurrentlyAllSelected = isAllSelected(subTopic.sub_topic_id, videos, files, notebooks);
    setSelectedItemsData(prev => {
      const newData = { ...prev };
      if (isCurrentlyAllSelected) {
        delete newData[subTopic.sub_topic_id];
      } else {
        newData[subTopic.sub_topic_id] = {
          videos: {},
          files: {},
          notebooks: {},
          topic: selectedTopic.topic_name || "",
          topicId: selectedTopic.topic_id || "",
          subtopicName: subTopic.sub_topic_name,
          mcq: calculateMaxCounts(subTopic).mcq,
          coding: calculateMaxCounts(subTopic).coding,
          optionalMcq: {},
          optionalCoding: {}
        };
        if (videos) {
          Object.entries(videos).forEach(([key, value]) => {
            // Since we only store content_id and time, we need to check level from the original contentData
            const originalVideo = contentData.videos[key];
            if (originalVideo && (topicLevels.includes(originalVideo.level) || topicLevels.length === 0)) {
              newData[subTopic.sub_topic_id].videos[key] = {
                content_id: originalVideo.content_id || 0,
                time: originalVideo.time
              };
            }
          });
        }
        if (files) {
          Object.entries(files).forEach(([key, value]) => {
            // Since we only store content_id and time, we need to check level from the original contentData
            const originalFile = contentData.files[key];
            if (originalFile && (topicLevels.includes(originalFile.level) || topicLevels.length === 0)) {
              newData[subTopic.sub_topic_id].files[key] = {
                content_id: originalFile.content_id || 0,
                time: originalFile.time
              };
            }
          });
        }
        if (notebooks) {
          Object.entries(notebooks).forEach(([key]) => {
            const originalNotebook = contentData.notebooks[key];
            if (originalNotebook) {
              newData[subTopic.sub_topic_id].notebooks[key] = {
                content_id: originalNotebook.content_id || 0,
                time: originalNotebook.time || "0",
              };
            }
          });
        }
      }
      return newData;
    });
  
    setSelectedMcqCoding(prev => ({
      ...prev,
      [subTopic.sub_topic_id]: {
        mcq: !isCurrentlyAllSelected,
        coding: !isCurrentlyAllSelected,
        optionalMcq: false,
        optionalCoding: false
      }
    }));
  
    setInputCounts(prev => ({
      ...prev,
      [subTopic.sub_topic_id]: {
        mcq: !isCurrentlyAllSelected ? calculateMaxCounts(subTopic).mcq : {},
        coding: !isCurrentlyAllSelected ? calculateMaxCounts(subTopic).coding : {},
        optionalMcq: {},
        optionalCoding: {}
      }
    }));
  };
  

  const handleCountChange = (subTopic: SubTopic, type: McqCodingType, level: string, value: string) => {
    // Prevent changes for restricted subtopics
    if (isContentSelectionDisabled(subTopic.sub_topic_id)) {
      return;
    }

    const numValue = Math.max(0, parseInt(value) || 0);
    
    // Validate the count based on available questions and optional content
    if (!validateCounts(subTopic.sub_topic_id, type, level, numValue)) {
      return; // Don't update if validation fails
    }
    
    const updatedMcqCoding = selectedMcqCoding[subTopic.sub_topic_id] || { mcq: false, coding: false, optionalMcq: false, optionalCoding: false };

    setInputCounts(prev => ({
      ...prev,
      [subTopic.sub_topic_id]: {
        ...prev[subTopic.sub_topic_id],
        [type]: {
          ...prev[subTopic.sub_topic_id]?.[type],
          [level]: numValue
        }
      }
    }));

    if (updatedMcqCoding[type]) {
      setSelectedItemsData(prev => ({
        ...prev,
        [subTopic.sub_topic_id]: {
          ...prev[subTopic.sub_topic_id] || {
            subtopicName: subTopic.sub_topic_name,
            videos: {},
            files: {},
            notebooks: {},
            topic: selectedTopic.topic_name || "",
            topicId: selectedTopic.topic_id || "",
            mcq: {},
            coding: {},
            optionalMcq: {},
            optionalCoding: {}
          },
          [type]: {
            ...prev[subTopic.sub_topic_id]?.[type],
            [level]: numValue
          }
        }
      }));
    }
  };

  const isItemSelected = (itemKey: string, type: string, subTopicId: string) => {
    const collection =
      type === "video" ? "videos" : type === "notebook" ? "notebooks" : "files";
    const items = selectedItemsData[subTopicId]?.[collection];
    if (!items) return false;
    if (items[itemKey] !== undefined) return true;

    // Match saved items still keyed by content_id before re-key effect runs
    const contentItem =
      type === "video"
        ? contentData.videos?.[itemKey]
        : type === "notebook"
          ? contentData.notebooks?.[itemKey]
          : contentData.files?.[itemKey];
    if (!contentItem?.content_id) return false;

    return Object.values(items).some((item) => item.content_id === contentItem.content_id);
  };

  const handleConfirmClick = () => {
    const hasSelectedItems = Object.values(selectedItemsData).some(subtopic =>
      Object.keys(subtopic.videos).length > 0 ||
      Object.keys(subtopic.files).length > 0 ||
      Object.keys(subtopic.notebooks || {}).length > 0 ||
      Object.keys(subtopic.mcq).length > 0 ||
      Object.keys(subtopic.coding).length > 0 ||
      Object.keys(subtopic.optionalMcq).length > 0 ||
      Object.keys(subtopic.optionalCoding).length > 0
    );
    if (hasSelectedItems) {
      const filteredData = Object.entries(selectedItemsData).reduce((acc, [subtopicId, data]) => {
        const filteredVideos = Object.entries(data.videos || {}).reduce((videosAcc, [contentId, video]) => {
          // For existing data, we don't need to check against contentData since it might not be loaded
          // Just include all videos that have content_id and time
          if (video.content_id) {
            // Treat empty or zero time as "0"
            const timeValue = video.time && video.time !== "" ? video.time : "0";
            videosAcc[contentId] = { ...video, time: timeValue };
          }
          return videosAcc;
        }, {} as { [key: string]: SelectedContentItem });

        const filteredFiles = Object.entries(data.files || {}).reduce((filesAcc, [contentId, file]) => {
          // For existing data, we don't need to check against contentData since it might not be loaded
          // Just include all files that have content_id and time
          if (file.content_id) {
            // Treat empty or zero time as "0"
            const timeValue = file.time && file.time !== "" ? file.time : "0";
            filesAcc[contentId] = { ...file, time: timeValue };
          }
          return filesAcc;
        }, {} as { [key: string]: SelectedContentItem });

        const filteredNotebooks = Object.entries(data.notebooks || {}).reduce(
          (notebooksAcc, [contentId, notebook]) => {
            if (notebook.content_id) {
              const timeValue =
                notebook.time && notebook.time !== "" ? notebook.time : "0";
              notebooksAcc[contentId] = { ...notebook, time: timeValue };
            }
            return notebooksAcc;
          },
          {} as { [key: string]: SelectedContentItem }
        );

        const filteredMcq = topicLevels.reduce((mcqAcc, level) => {
          mcqAcc[level] = data.mcq?.[level] || 0;
          return mcqAcc;
        }, {} as { [key: string]: number });

        const filteredCoding = topicLevels.reduce((codingAcc, level) => {
          codingAcc[level] = data.coding?.[level] || 0;
          return codingAcc;
        }, {} as { [key: string]: number });

        const filteredOptionalMcq = topicLevels.reduce((optionalMcqAcc, level) => {
          optionalMcqAcc[level] = data.optionalMcq?.[level] || 0;
          return optionalMcqAcc;
        }, {} as { [key: string]: number });

        const filteredOptionalCoding = topicLevels.reduce((optionalCodingAcc, level) => {
          optionalCodingAcc[level] = data.optionalCoding?.[level] || 0;
          return optionalCodingAcc;
        }, {} as { [key: string]: number });

        if (
          Object.keys(filteredVideos).length > 0 ||
          Object.keys(filteredFiles).length > 0 ||
          Object.keys(filteredNotebooks).length > 0 ||
          Object.values(filteredMcq).some(count => count > 0) ||
          Object.values(filteredCoding).some(count => count > 0) ||
          Object.values(filteredOptionalMcq).some(count => count > 0) ||
          Object.values(filteredOptionalCoding).some(count => count > 0)
        ) {
          acc[subtopicId] = {
            ...data,
            videos: filteredVideos,
            files: filteredFiles,
            notebooks: filteredNotebooks,
            mcq: filteredMcq,
            coding: filteredCoding,
            optionalMcq: filteredOptionalMcq,
            optionalCoding: filteredOptionalCoding
          };
        }

        return acc;
      }, {} as SelectedItemsData);

      const summaryData = Object.entries(filteredData).reduce((acc, [subtopicId, data]) => {
        const topic = reorderedTopics.find(t => t.topic_id === data.topicId);
        if (!acc[data.topicId]) {
          acc[data.topicId] = {
            id: data.topicId,
            name: data.topic,
            subtopics: []
          };
        }
        
        const videos = Object.entries(data.videos || {}).map(([videoKey, video]) => {
          return {
            type: 'video',
            content_id: video.content_id,
            time: video.time
          };
        });

        const files = Object.entries(data.files || {}).map(([fileKey, file]) => {
          return {
            type: 'file',
            content_id: file.content_id,
            time: file.time
          };
        });

        const notebooks = Object.entries(data.notebooks || {}).map(([notebookKey, notebook]) => ({
          type: 'notebook' as const,
          content_id: notebook.content_id,
          time: notebook.time || '0',
        }));

        // Combine videos, files, and notebooks into a single data array
        const dataArray = [
          ...videos.map(video => ({
            time: video.time,
            type: video.type,
            content_id: video.content_id
          })),
          ...files.map(file => ({
            time: file.time,
            type: file.type,
            content_id: file.content_id
          })),
          ...notebooks.map(notebook => ({
            time: notebook.time,
            type: notebook.type,
            content_id: notebook.content_id
          }))
        ];

        // Find the subtopic in the reordered list to maintain order
        const reorderedSubtopic = reorderedSubTopics[data.topicId]?.find(sub => sub.sub_topic_id === subtopicId);
        const subtopicIndex = reorderedSubtopic ? reorderedSubTopics[data.topicId].indexOf(reorderedSubtopic) : acc[data.topicId].subtopics.length;
        
        const subtopicData = {
          id: subtopicId,
          subtopicName: data.subtopicName || reorderedSubtopic?.sub_topic_name || "",
          data: dataArray,
          mcq: data.mcq || {},
          coding: data.coding || {},
          optionalMcq: data.optionalMcq || {},
          optionalCoding: data.optionalCoding || {},
        } as { id: string; subtopicName: string; data: { time: string; type: string; content_id: number }[]; mcq: { [key: string]: number }; coding: { [key: string]: number }; optionalMcq: { [key: string]: number }; optionalCoding: { [key: string]: number } };
        
        // Insert at the correct position to maintain order
        acc[data.topicId].subtopics.splice(subtopicIndex, 0, subtopicData);

        return acc;
      }, {} as { [key: string]: { id: string; name: string; subtopics: { id: string; subtopicName: string; data: { time: string; type: string; content_id: number }[]; mcq: { [key: string]: number }; coding: { [key: string]: number }; optionalMcq: { [key: string]: number }; optionalCoding: { [key: string]: number } }[] } });

      // Create structured data maintaining the reordered topic and subtopic order
      // Send only topics that have subtopics with data
      const orderedTopics = reorderedTopics.map(topic => {
        // Get all subtopics in the reordered sequence for this topic
        const reorderedSubtopics = reorderedSubTopics[topic.topic_id] || [];
        
        // Filter to only include subtopics that have actual data
        const subtopicsWithData = reorderedSubtopics
          .map(subtopic => {
            const subtopicData = summaryData[topic.topic_id]?.subtopics.find(sub => sub.id === subtopic.sub_topic_id);
            return subtopicData; // This will be undefined if no data
          })
          .filter(subtopic => subtopic !== undefined); // Remove undefined entries
        
        // Only include topics that have subtopics with data
        if (subtopicsWithData.length > 0) {
          return {
            id: topic.topic_id,
            name: topic.topic_name,
            subtopics: subtopicsWithData
          };
        }
        return null; // This topic has no data
      }).filter(topic => topic !== null); // Remove topics with no data

      const structuredData = {
        course: selectedCourse,
        subject_id: selectedSubject.subject_id,
        subject_name: selectedSubject.subject_name,
        topics: orderedTopics
      };

      navigate('/admin/course-live-sessions', {
        state: {
          selectedItems: structuredData,
          subjectPlan: subjectPlan,
          time:time
        }
      });
    }
  };

  const fetchData = async () => {
    try {
      const response = await authService.get("admin/fetch_rules/");
      setTimeData(response.data); 
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };
  useEffect(() => {
    fetchCourses();
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedsub.sub_topic_id !== "") {
      setContentData({ videos: {}, files: {}, notebooks: {} });
      fetchContent();
      setCount({ mcq: {}, coding: {} });
      fetchCount();
    }
  }, [selectedsub]);

  const fetchContent = async () => {
    if (!selectedsub.sub_topic_id) return;
    setFetchContentLoading(true);
    try {
      const response = await authService.post(
        `admin/content/get_content_for_subtopic/`,
        { sub_topic_id: selectedsub.sub_topic_id }
      );
      if (response.status === 200) {
        const content = response.data;
        const videos: { [key: string]: ContentItem } = {};
        const files: { [key: string]: ContentItem } = {};
        const notebooks: { [key: string]: ContentItem } = {};
        content.videos && Object.keys(content.videos).forEach(key => {
          videos[key] = { ...content.videos[key], type: 'video' };
        });
        content.files && Object.keys(content.files).forEach(key => {
          files[key] = { ...content.files[key], type: 'file' };
        });
        content.notebooks && Object.keys(content.notebooks).forEach(key => {
          notebooks[key] = { ...content.notebooks[key], type: 'notebook' };
        });
        setContentData({ videos, files, notebooks });
        const availableNotebookIds = new Set(
          Object.values(notebooks).map((item) => String(item.content_id))
        );
        setSelectedItemsData((prev) => {
          const current = prev[selectedsub.sub_topic_id];
          if (!current?.notebooks) return prev;
          const nextNotebooks: { [key: string]: SelectedContentItem } = {};
          Object.entries(current.notebooks).forEach(([key, item]) => {
            if (availableNotebookIds.has(String(item.content_id))) {
              nextNotebooks[key] = item;
            }
          });
          if (Object.keys(nextNotebooks).length === Object.keys(current.notebooks).length) {
            return prev;
          }
          return {
            ...prev,
            [selectedsub.sub_topic_id]: { ...current, notebooks: nextNotebooks },
          };
        });
      }
      setFetchContentLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setFetchContentLoading(false);
    }
  };

  const fetchCount = async () => {
    if (!selectedsub.sub_topic_id) return;
    setFetchCountLoading(true);
    try {
      const response = await authService.post(
        `admin/content/get_questions_data_by_subtopic/`,
        { sub_topic_id: selectedsub.sub_topic_id }
      );
      if (response.status === 200) {
        setCount(response.data);
      }
      setFetchCountLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setFetchCountLoading(false);
    }
  };

  useEffect(() => {
    setTopics([]);
    setSelectedTopic({ topic_id: "", topic_name: "" });
    setSubTopicData([]);
    setSelectedsub({ sub_topic_id: "", sub_topic_name: "", notes: 0, videos: 0, mcq: 0, coding: 0 });
    setContentData({ videos: {}, files: {}, notebooks: {} });
    setInputCounts({});
    setSelectedItemsData({});
    setSelectedMcqCoding({});
    setCount({ mcq: {}, coding: {} });

    fetchTopics(selectedSubject.subject_id);
  }, [selectedSubject]);

  useEffect(() => {
    setSelectedSubject({ subject_id: "", subject_name: "" });
    setTopics([]);
    setSelectedTopic({ topic_id: "", topic_name: "" });
    setSubTopicData([]);
    setSelectedsub({ sub_topic_id: "", sub_topic_name: "", notes: 0, videos: 0, mcq: 0, coding: 0 });
    setContentData({ videos: {}, files: {}, notebooks: {} });
    setInputCounts({});
    setSelectedItemsData({});
    setSelectedMcqCoding({});
    setCount({ mcq: {}, coding: {} });
    setExistingData([]);
  }, [selectedCourse]);

  useEffect(() => {
    setContentData({ videos: {}, files: {}, notebooks: {} });
    setSelectedsub({ sub_topic_id: "", sub_topic_name: "", notes: 0, videos: 0, mcq: 0, coding: 0 });
    fetchSubTopics(selectedTopic.topic_id);
  }, [selectedTopic]);

  const handleSubjectClick = async (subject: Subject) => {
    if (!subject) {
      setSelectedSubject({ subject_id: "", subject_name: "" });
      return;
    }
    const selectedValue = subject.subject_id;

    const selectedSubjectObj = subjects.find(sub => sub.subject_id === selectedValue);
    setSelectedSubject({
      subject_id: selectedSubjectObj!.subject_id,
      subject_name: selectedSubjectObj!.subject_name
    });
    
    // Fetch topics in correct order and restricted subtopics when subject is selected
    fetchTopicsInOrder(selectedCourse.course_id, subject.subject_id);
    fetchRestrictedSubtopics(selectedCourse.course_id, subject.subject_id);
    
    const existing = existSubjects.includes(selectedValue);
    if (existing) {
      const json = {
        course_id: selectedCourse.course_id,
        subject_id: subject.subject_id
      };
      try {
        const response = await authService.post("admin/get_all_course_subjects/",json);
        if (response.status === 200) {
          setExistingData(response.data.topics);
        }
      } catch (error) {
        console.error("Error adding new subject plan:", error);
      }
    }
  };

  const handleCourseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    if (!selectedValue) {
      setSelectedCourse({ course_id: "", course_name: "", subjects: [] ,level:"", Existing_Subjects:[]});
      return;
    }
    const selectedSubjectObj = courses.find(course => course.course_id === selectedValue);
    if (selectedSubjectObj) {
      setSelectedCourse({
        course_id: selectedSubjectObj.course_id,
        course_name: selectedSubjectObj.course_name,
        subjects: selectedSubjectObj.subjects,
        level: selectedSubjectObj.level,
        Existing_Subjects: selectedSubjectObj.Existing_Subjects
      });
      setSubjects(selectedSubjectObj.subjects);
      setExistSubjects(selectedSubjectObj.Existing_Subjects)
      setTopicLevels(selectedSubjectObj.level.split(","));
    }
  };

  const fetchSubTopics = async (topic: string) => {
    if (!topic) return;
    try {
      setFetchSubTopicLoading(true);
      const response = await authService.get(
        `admin/content/get_all_subtopics_data/${topic}/`
      );
      setSubTopicData(response.data.subtopics);
      setReorderedSubTopics(prev => ({
        ...prev,
        [topic]: response.data.subtopics
      }));
      setFetchSubTopicLoading(false);
    } catch (error) {
      console.error("Error fetching topics:", error);
      setFetchSubTopicLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setFetchCourseLoading(true);
      const response = await authService.get("admin/get_all_course_tracks_and_subjects/");
      setCourses(response.data.courses);
      setFetchCourseLoading(false);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setFetchCourseLoading(false);
    }
  };

  const fetchTopics = async (subjectId: string) => {
    if (!subjectId) return;
    try {
      setFetchTopicLoading(true);
      const response = await authService.get(`admin/topics_by_subject/${subjectId}/`);
      setTopics(response.data.topics);
      setReorderedTopics(response.data.topics);
      setFetchTopicLoading(false);
    } catch (error) {
      console.error("Error fetching topics:", error);
      setFetchTopicLoading(false);
    }
  };

  // Fetch topics from the existing subjects API to get the correct order
  const fetchTopicsInOrder = async (courseId: string, subjectId: string) => {
    if (!courseId || !subjectId) return;
    try {
      setFetchTopicLoading(true);
      
      // First, get topics with content from get_all_course_subjects
      const contentResponse = await authService.post("admin/get_all_course_subjects/", {
        course_id: courseId,
        subject_id: subjectId
      });
      
      // Then, get all topics from topics_by_subject
      const allTopicsResponse = await authService.get(`admin/topics_by_subject/${subjectId}/`);
      
      if (contentResponse.status === 200 && allTopicsResponse.status === 200) {
        // Get topics with content (from get_all_course_subjects)
        const topicsWithContent = contentResponse.data.topics.map((topic: any) => ({
          topic_id: topic.id,
          topic_name: topic.name
        }));
        
        // Get all topics (from topics_by_subject)
        const allTopics = allTopicsResponse.data.topics;
        
        // Find topics without content (not in the content response)
        const topicsWithContentIds = contentResponse.data.topics.map((topic: any) => topic.id);
        const topicsWithoutContent = allTopics.filter((topic: any) => 
          !topicsWithContentIds.includes(topic.topic_id)
        );
        
        // Combine: topics with content first, then topics without content
        const orderedTopics = [...topicsWithContent, ...topicsWithoutContent];
        
        setTopics(orderedTopics);
        setReorderedTopics(orderedTopics);
        
        // Set subtopics data for topics with content
        const subtopicsData: { [topicId: string]: SubTopic[] } = {};
        contentResponse.data.topics.forEach((topic: any) => {
          subtopicsData[topic.id] = topic.subtopics.map((subtopic: any) => ({
            sub_topic_id: subtopic.id,
            sub_topic_name: subtopic.subtopicName,
            notes: 0, // Default values
            videos: 0,
            mcq: 0,
            coding: 0
          }));
        });
        setReorderedSubTopics(subtopicsData);
      }
      setFetchTopicLoading(false);
    } catch (error) {
      console.error("Error fetching topics in order:", error);
      setFetchTopicLoading(false);
    }
  };

  const fetchRestrictedSubtopics = async (courseId: string, subjectId: string) => {
    if (!courseId || !subjectId) return;
    try {
      setFetchRestrictedLoading(true);
      const response = await authService.get(`admin/batch/subtopics_for_max_day/${courseId}/${subjectId}/`);
      const restrictedSubtopics = response.data.batch_subtopics || [];
      setRestrictedSubtopicIds(restrictedSubtopics);
      
      // Extract topic IDs from subtopic IDs (subtopic ID = topic_id + 2 extra characters)
      const topicIds = new Set<string>();
      restrictedSubtopics.forEach((subtopicId: string) => {
        // Remove the last 2 characters to get the topic ID
        const topicId = subtopicId.substring(0, subtopicId.length - 2);
        topicIds.add(topicId);
      });
      const extractedTopicIds = Array.from(topicIds);
      setRestrictedTopicIds(extractedTopicIds);
      
      setFetchRestrictedLoading(false);
    } catch (error) {
      console.error("Error fetching restricted subtopics:", error);
      setFetchRestrictedLoading(false);
    }
  };

  const toggleTopicContent = (topic: Topic) => {
    setSelectedTopic((prevTopic) => (prevTopic.topic_id === topic.topic_id ? { topic_id: "", topic_name: "" } : { topic_id: topic.topic_id, topic_name: topic.topic_name }));
  };

  const handleLevelChange = (level: string) => {
    setTopicLevels((prevLevels) => {
      const currentLevels = prevLevels || [];
      if (currentLevels.includes(level)) {
        return currentLevels.filter(l => l !== level);
      } else {
        return [...currentLevels, level];
      }
    });
  };

  // Check if a subtopic is restricted
  const isSubtopicRestricted = (subtopicId: string) => {
    // The API returns the actual subtopic IDs (topic_id + 2 extra characters)
    // We check if the current subtopic ID is directly in the restricted list
    return restrictedSubtopicIds.includes(subtopicId);
  };

  // Check if content selection should be disabled for a subtopic
  const isContentSelectionDisabled = (subtopicId: string) => {
    return isSubtopicRestricted(subtopicId);
  };

  // Check if a topic has any restricted subtopics
  const hasRestrictedSubtopics = (topicId: string) => {
    // Check if the topic ID is in the restricted topic IDs list
    return restrictedTopicIds.includes(topicId);
  };

  // Handle topic reordering
  const handleTopicDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(reorderedTopics);
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const sourceItem = items[sourceIndex];
    const destinationItem = items[destinationIndex];

    // Clear any previous error
    setDragError(null);

    // Prevent moving non-restricted topics into restricted topic positions
    // and prevent moving restricted topics
    if (hasRestrictedSubtopics(sourceItem.topic_id)) {
      // Restricted topics cannot be moved
      setDragError("Cannot move restricted topics!");
      setTimeout(() => setDragError(null), 3000);
      return;
    }

    if (hasRestrictedSubtopics(destinationItem.topic_id)) {
      // Cannot move non-restricted topics into restricted positions
      setDragError("Cannot move topics into restricted positions!");
      setTimeout(() => setDragError(null), 3000);
      return;
    }

    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    setReorderedTopics(items);
  };

  // Handle subtopic reordering
  const handleSubtopicDragEnd = (result: any, topicId: string) => {
    if (!result.destination) return;

    const topicSubtopics = reorderedSubTopics[topicId] || [];
    const items = Array.from(topicSubtopics);
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const sourceItem = items[sourceIndex];
    const destinationItem = items[destinationIndex];

    // Clear any previous error
    setDragError(null);

    // Prevent moving non-restricted subtopics into restricted subtopic positions
    // and prevent moving restricted subtopics
    if (isSubtopicRestricted(sourceItem.sub_topic_id)) {
      // Restricted subtopics cannot be moved
      setDragError("Cannot move restricted subtopics!");
      setTimeout(() => setDragError(null), 3000);
      return;
    }

    if (isSubtopicRestricted(destinationItem.sub_topic_id)) {
      // Cannot move non-restricted subtopics into restricted positions
      setDragError("Cannot move subtopics into restricted positions!");
      setTimeout(() => setDragError(null), 3000);
      return;
    }

    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    setReorderedSubTopics(prev => ({
      ...prev,
      [topicId]: items
    }));
  };

  const calculateMaxMcqCount = (subTopic: SubTopic): { [key: string]: number } => {
    const selectedLevels = topicLevels.filter(level => count?.mcq?.[level] !== undefined);
    return selectedLevels.reduce((sum, level) => ({ ...sum, [level]: count?.mcq?.[level] || 0 }), {});
  };

  const calculateMaxCodingCount = (subTopic: SubTopic): { [key: string]: number } => {
    const selectedLevels = topicLevels.filter(level => count?.coding?.[level] !== undefined);
    return selectedLevels.reduce((sum, level) => ({ ...sum, [level]: count?.coding?.[level] || 0 }), {});
  };

  const buildVideoUrl = (lesson: { otp: string; playback_info: string }): string => {
    return `https://player.vdocipher.com/v2/?otp=${lesson.otp}&playbackInfo=${lesson.playback_info}`;
  };

  const handlePreviewClick = async (type: "video" | "file" | "notebook", contentItem: ContentItem) => {
    setModalContent(null);
    setModalError(null);
    setNotebookPreviewModal(type === "notebook");
    setShowModal(true);

    if (type === "notebook" && contentItem.content_id) {
      try {
        const detailResponse = await authService.post(
          "content_creation/notebooks/detail/",
          { notebook_id: contentItem.content_id }
        );
        const source = detailResponse.data?.source;
        if (!source) {
          setModalError("Preview not available for this notebook.");
          return;
        }
        const previewResponse = await authService.post(
          "content_creation/notebooks/preview/",
          { source, notebook_id: contentItem.content_id }
        );
        const cells = Array.isArray(previewResponse.data?.cells) && previewResponse.data.cells.length
          ? previewResponse.data.cells
          : cellsFromNotebookSource(source);
        if (!cells.length) {
          setModalError("Preview not available for this notebook.");
          return;
        }
        setModalContent(
          <div style={{ height: "75vh" }}>
            <NotebookStudentPreview
              title={detailResponse.data?.title || contentItem.text || "Notebook"}
              source={source}
              cells={cells}
            />
          </div>
        );
      } catch (error: unknown) {
        const detail =
          (error as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail;
        setModalError(
          detail
            ? `Preview failed: ${detail}`
            : "Preview not available for this notebook."
        );
      }
      return;
    }

    if (type === "video" && contentItem.otp && contentItem.playback_info) {
      const url = buildVideoUrl({ otp: contentItem.otp, playback_info: contentItem.playback_info });
      
      // Create a more robust video player with fallback options
      const videoPlayer = (
        <div>
          <iframe
            src={url}
            width="100%"
            height="500px"
            title="Video Preview"
            allowFullScreen
            allow="encrypted-media; autoplay; clipboard-write"
            style={{ border: "none" }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      );
      
      setModalContent(videoPlayer);
    } else if (type === "file" && contentItem.content) {
      setModalContent(
        <div className='p-0 m-0 ps-3'
          style={{
            fontSize: "16px",
            lineHeight: "1.6",
            margin: "40px",
            marginTop: "0px",
            paddingBottom: "100px",
            color: "#333",
            height: "100%",
            overflow: "auto"
          }}
          dangerouslySetInnerHTML={{ __html: `
            <style>
              h1, h2 {
                font-weight: 600;
                color: #2C3E50;
                margin-top: 30px;
              }
              ul {
                margin: 10px 0 20px 20px;
              }
              li {
                margin-bottom: 8px;
              }
              code {
                background-color: #F4F4F4;
                padding: 4px 6px;
                border-radius: 4px;
              }
              table {
                border-collapse: collapse;
                width: 100%;
                margin: 20px 0;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 12px;
                text-align: left;
              }
              th {
                background-color: #F0F0F0;
              }
              pre {
                background-color: #F8F8F8;
                padding: 12px;
                border-left: 3px solid #ccc;
                font-size: 15px;
                overflow-x: auto;
              }
            </style>
            ${contentItem.content}
          `}}
        />
      );
    } else {
      setModalError("Preview not available for this content.");
    }
  };

  return (
    <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
    style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }} >
      <div >
        <div className="container-fluid">
          <div className="row d-flex mb-2 justify-content-between align-items-center">
            <div className="col d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center"><label className="ps-1 me-2">Select Course : </label>
                <select
                  className="form-select form-select-sm"
                  id="course"
                  onChange={handleCourseChange}
                  value={selectedCourse.course_id || ""}
                  style={{ width: "150px" }}
                >
                  <option value="">Select course</option>
                  {courses.map((course) => (
                    <option
                      key={course.course_id}
                      value={course.course_id}
                    >
                      {course.course_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <label className="me-2">Select Subject : </label>
                <select
                  className="form-select form-select-sm"
                  id="subject"
                  value={selectedSubject.subject_id || ""}
                  style={{ width: "150px" }}
                  onChange={(e) => handleSubjectClick(subjects.find(sub => sub.subject_id === e.target.value) as Subject)}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option
                      key={subject.subject_id}
                      value={subject.subject_id}
                      className={existSubjects.includes(subject.subject_id) ? "text-success" : ""}
                      style={{
                        backgroundColor: existSubjects.includes(subject.subject_id) ? "#f5f5f5" : "inherit"
                      }}
                    >
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <label className="me-2">Level : </label>
                <div className="">
                  <div
                    className=""
                    style={{ width: "160px" }}
                  >
                    {topicLevels.length > 0 ? topicLevels.join(", ") : "Select Level"}
                  </div>
                  <ul className="dropdown-menu p-0 m-0" aria-labelledby="dropdownMenuButton">
                    <li>
                      <label className="dropdown-item">
                        <input
                          type="checkbox"
                          checked={topicLevels.includes("level1")}
                          onChange={() => handleLevelChange("level1")}
                          className="me-2"
                        />
                        Level 1
                      </label>
                    </li>
                    <li>
                      <label className="dropdown-item">
                        <input
                          type="checkbox"
                          checked={topicLevels.includes("level2")}
                          onChange={() => handleLevelChange("level2")}
                          className="me-2"
                        />
                        Level 2
                      </label>
                    </li>
                    <li>
                      <label className="dropdown-item">
                        <input
                          type="checkbox"
                          checked={topicLevels.includes("level3")}
                          onChange={() => handleLevelChange("level3")}
                          className="me-2"
                        />
                        Level 3
                      </label>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <label className="me-2">Time : </label>
                <span className="fs-6">
                  {Math.floor(time / 60)} hr {(time % 60).toString().padStart(2, "0")} mins
                </span>
              </div>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleConfirmClick}
                disabled={!Object.values(selectedItemsData).some(
                  (subtopic) =>
                    Object.keys(subtopic.videos).length > 0 ||
                    Object.keys(subtopic.files).length > 0 ||
                    Object.keys(subtopic.mcq).length > 0 ||
                    Object.keys(subtopic.coding).length > 0 ||
                    Object.keys(subtopic.optionalMcq).length > 0 ||
                    Object.keys(subtopic.optionalCoding).length > 0
                )}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>

        {dragError && (
          <div className="alert alert-warning alert-dismissible fade show mx-3 mt-2" role="alert">
            <strong>Warning:</strong> {dragError}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setDragError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {reorderedTopics.length > 0 && (
          <div className="mt-3 mx-3">
            <DragDropContext onDragEnd={handleTopicDragEnd}>
              <Droppable droppableId="topics">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                    {reorderedTopics.map((topic, index) => (
                      <Draggable 
                        key={topic.topic_id} 
                        draggableId={topic.topic_id} 
                        index={index}
                        isDragDisabled={hasRestrictedSubtopics(topic.topic_id)}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`border border-gray-300 rounded-lg mb-3 shadow-sm ${hasRestrictedSubtopics(topic.topic_id) ? 'opacity-75' : ''}`}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white'
                            }}
                          >
                            <div 
                              className="ps-3 fs-5 py-2 d-flex justify-content-between align-items-center bg-light" 
                              onClick={() => toggleTopicContent(topic)} 
                              style={{ 
                                cursor: "pointer"
                              }}
                            >
                              <div className="d-flex align-items-center">
                                {!hasRestrictedSubtopics(topic.topic_id) && (
                                  <div 
                                    {...provided.dragHandleProps}
                                    className="me-2"
                                    style={{ cursor: 'grab' }}
                                  >
                                    <FaGripVertical />
                                  </div>
                                )}
                                {hasRestrictedSubtopics(topic.topic_id) && (
                                  <div className="me-2">
                                    <FaLock />
                                  </div>
                                )}
                                <span className="fw-medium">{topic.topic_name}</span>
                              </div>
                              <div className="d-flex align-items-center">
                                <span
                                  role="button"
                                  className="px-2"
                                  style={{ cursor: "pointer" }}
                                >
                                  {selectedTopic.topic_id === topic.topic_id ? "^" : "v"}
                                </span>
                              </div>
                            </div>
                            {selectedTopic.topic_id === topic.topic_id && (
                              <div className="mt-2">
                                {reorderedSubTopics[topic.topic_id] && reorderedSubTopics[topic.topic_id].length > 0 && (
                                  <DragDropContext onDragEnd={(result) => handleSubtopicDragEnd(result, topic.topic_id)}>
                                    <Droppable droppableId={`subtopics-${topic.topic_id}`}>
                                      {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                          {reorderedSubTopics[topic.topic_id].map((subTopic, subIndex) => (
                                            <Draggable 
                                              key={subTopic.sub_topic_id} 
                                              draggableId={subTopic.sub_topic_id} 
                                              index={subIndex}
                                              isDragDisabled={isSubtopicRestricted(subTopic.sub_topic_id)}
                                            >
                                              {(provided, snapshot) => (
                                                <div
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  className={`mx-2 mb-2 border border-gray-200 rounded-lg ${isSubtopicRestricted(subTopic.sub_topic_id) ? 'opacity-75' : ''}`}
                                                  style={{
                                                    ...provided.draggableProps.style,
                                                    backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white'
                                                  }}
                                                >
                                                  <div className="d-flex justify-content-between align-items-center py-2 px-3">
                                                    <div className="d-flex align-items-center">
                                                      {!isSubtopicRestricted(subTopic.sub_topic_id) && (
                                                        <div 
                                                          {...provided.dragHandleProps}
                                                          className="me-2"
                                                          style={{ cursor: 'grab' }}
                                                        >
                                                          <FaGripVertical />
                                                        </div>
                                                      )}
                                                      {isSubtopicRestricted(subTopic.sub_topic_id) && (
                                                        <div className="me-2">
                                                          <FaLock />
                                                        </div>
                                                      )}
                                                      <span className="ps-2">
                                                        {selectedsub.sub_topic_id === subTopic.sub_topic_id && (
                                                          <input
                                                            type="checkbox"
                                                            className="me-2"
                                                            checked={isAllSelected(subTopic.sub_topic_id, contentData.videos || {}, contentData.files || {}, contentData.notebooks || {})}
                                                            onChange={() => handleSelectAllChange(subTopic, contentData.videos || {}, contentData.files || {}, contentData.notebooks || {})}
                                                            disabled={isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                          />
                                                        )}
                                                        <span 
                                                          onClick={() => toggleSubTopicContent(subTopic)}
                                                          style={{ cursor: "pointer" }}
                                                        >
                                                          {subTopic.sub_topic_name}
                                                          {isSubtopicRestricted(subTopic.sub_topic_id) && (
                                                            <small className="ms-2 text-muted">(Restricted)</small>
                                                          )}
                                                        </span>
                                                      </span>
                                                    </div>

                                                    <div onClick={() => toggleSubTopicContent(subTopic)} className="d-flex justify-content-between">
                                                      <div className="px-2 text-center">
                                                        <span>Videos</span> <br />
                                                        <span>{subTopic.videos || 0}</span>
                                                      </div>
                                                      <div className="px-2 text-center">
                                                        <span>Notes</span> <br />
                                                        <span>{subTopic.notes || 0}</span>
                                                      </div>
                                                      <div className="px-2 text-center">
                                                        <span>MCQ</span> <br />
                                                        <span>{subTopic.mcq || 0}</span>
                                                      </div>
                                                      <div className="px-2 text-center">
                                                        <span>Coding</span> <br />
                                                        <span>{subTopic.coding || 0}</span>
                                                      </div>
                                                      <div className="px-2 text-center">
                                                        <span>Notebooks</span> <br />
                                                        <span>{subTopic.notebooks || 0}</span>
                                                      </div>
                                                      <span role="button" className="me-3">
                                                        {selectedsub.sub_topic_id === subTopic.sub_topic_id ? "^" : "v"}
                                                      </span>
                                                    </div>
                                                  </div>

                                                  {selectedsub.sub_topic_id === subTopic.sub_topic_id && (
                                                    <div className="mt-2 p-2">
                                                      {contentData.videos && Object.keys(contentData.videos).length > 0 && (
                                                        <>
                                                          {Object.keys(contentData.videos).map((videoKey) => {
                                                            const video = contentData.videos[videoKey];
                                                            const level = video.level;
                                                            if (topicLevels.includes(level) || topicLevels.length === 0) {
                                                              return (
                                                                <div key={videoKey} className="d-flex justify-content-between mb-2">
                                                                  <span>
                                                                    <input
                                                                      type="checkbox"
                                                                      onChange={(e) => handleCheckboxChange(videoKey, "video", selectedsub, video, e.target.checked)}
                                                                      checked={isItemSelected(videoKey, "video", selectedsub.sub_topic_id)}
                                                                      disabled={isContentSelectionDisabled(selectedsub.sub_topic_id)}
                                                                    />
                                                                    <strong className="ms-2">{videoKey.toLocaleUpperCase()}</strong>{" "}{video.level}{" "} {video.text}
                                                                  </span>
                                                                  <span>
                                                                    <strong className="me-3">{video.time || "0"}m</strong>
                                                                    <button className="border border-none rounded-2" onClick={() => handlePreviewClick("video", video)} style={{ backgroundColor: "#F1F2FF" }}>
                                                                      Preview 
                                                                    </button>
                                                                  </span>
                                                                </div>
                                                              );
                                                            }
                                                          })}
                                                        </>
                                                      )}

                                                      {contentData.files && Object.keys(contentData.files).length > 0 && (
                                                        <>
                                                          {Object.keys(contentData.files).map((fileKey) => {
                                                            const file = contentData.files[fileKey];
                                                            const level = file.level;
                                                            if (topicLevels.includes(level) || topicLevels.length === 0) {
                                                              return (
                                                                <div key={fileKey} className="d-flex justify-content-between mb-2">
                                                                  <span>
                                                                    <input
                                                                      type="checkbox"
                                                                      onChange={(e) => handleCheckboxChange(fileKey, "file", selectedsub, file, e.target.checked)}
                                                                      checked={isItemSelected(fileKey, "file", selectedsub.sub_topic_id)}
                                                                      disabled={isContentSelectionDisabled(selectedsub.sub_topic_id)}
                                                                    />
                                                                    <strong className="ms-2">{fileKey.toLocaleUpperCase()}</strong> {" "}{file.level}{" "} {file.text}
                                                                  </span>
                                                                  <span>
                                                                    <strong className="me-3">{file.time || "0"}m</strong>
                                                                    <button className="border border-none rounded-2" onClick={() => handlePreviewClick("file", file)} style={{ backgroundColor: "#F1F2FF" }}>
                                                                      Preview
                                                                    </button>
                                                                  </span>
                                                                </div>
                                                              );
                                                            }
                                                          })}
                                                        </>
                                                      )}

                                                      {contentData.notebooks && Object.keys(contentData.notebooks).length > 0 && (
                                                        <>
                                                          {Object.keys(contentData.notebooks).map((notebookKey) => {
                                                            const notebook = contentData.notebooks[notebookKey];
                                                            return (
                                                              <div key={notebookKey} className="d-flex justify-content-between mb-2">
                                                                <span>
                                                                  <input
                                                                    type="checkbox"
                                                                    onChange={(e) => handleCheckboxChange(notebookKey, "notebook", selectedsub, notebook, e.target.checked)}
                                                                    checked={isItemSelected(notebookKey, "notebook", selectedsub.sub_topic_id)}
                                                                    disabled={isContentSelectionDisabled(selectedsub.sub_topic_id)}
                                                                  />
                                                                  <strong className="ms-2">{notebookKey.toLocaleUpperCase()}</strong>{" "}
                                                                  {notebook.text}
                                                                </span>
                                                                <span>
                                                                  <strong className="me-3">{notebook.time || "0"}m</strong>
                                                                  <button
                                                                    type="button"
                                                                    className="border border-none rounded-2"
                                                                    onClick={() => handlePreviewClick("notebook", notebook)}
                                                                    style={{ backgroundColor: "#F1F2FF" }}
                                                                  >
                                                                    Preview
                                                                  </button>
                                                                </span>
                                                              </div>
                                                            );
                                                          })}
                                                        </>
                                                      )}
                                                      <div className="row">
                                                        <div className="col-6">
                                                          <div className="me-4">
                                                            <input
                                                              type="checkbox"
                                                              checked={selectedMcqCoding[subTopic.sub_topic_id]?.mcq || false}
                                                              onChange={(e) => {
                                                                handleCheckboxChange(
                                                                  "mcq",            // item identifier (can be question ID or just "mcq")
                                                                  "mcq",            // type
                                                                  subTopic,         // subtopic
                                                                  undefined,        // itemData not needed for mcq
                                                                  e.target.checked  // actual checkbox state
                                                                );
                                                              }}
                                                              className="me-2"
                                                              disabled={isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                            />
                                                            <label>MCQ</label>
                                                            <br />
                                                            {topicLevels.map(level => (
                                                              <div key={level}>
                                                                <label>{level.toLocaleUpperCase()} :</label>
                                                                <input
                                                                  type="number"
                                                                  min="0"
                                                                  max={count?.mcq?.[level]}
                                                                  value={inputCounts[subTopic.sub_topic_id]?.mcq?.[level] || 0}
                                                                  onChange={(e) => handleCountChange(subTopic, "mcq", level, e.target.value)}
                                                                  className="ms-4"
                                                                  style={{ width: "60px" }}
                                                                  disabled={!selectedMcqCoding[subTopic.sub_topic_id]?.mcq || isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                                />
                                                                <span className="ms-2">/ {count?.mcq?.[level]}</span>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                        <div className="col-6">
                                                          <div className="me-4">
                                                            <input
                                                              type="checkbox"
                                                              checked={selectedMcqCoding[subTopic.sub_topic_id]?.optionalMcq || false}
                                                              onChange={(e) => {
                                                                handleCheckboxChange(
                                                                  "optionalMcq",    // item identifier
                                                                  "optionalMcq",    // type
                                                                  subTopic,         // subtopic
                                                                  undefined,        // itemData not needed for optional mcq
                                                                  e.target.checked  // actual checkbox state
                                                                );
                                                              }}
                                                              className="me-2"
                                                              disabled={isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                            />
                                                            <label>Optional MCQ</label>
                                                            <br />
                                                            {topicLevels.map(level => (
                                                              <div key={level}>
                                                                <label>{level.toLocaleUpperCase()} :</label>
                                                                <input
                                                                  type="number"
                                                                  min="0"
                                                                  max={count?.mcq?.[level]}
                                                                  value={inputCounts[subTopic.sub_topic_id]?.optionalMcq?.[level] || 0}
                                                                  onChange={(e) => handleCountChange(subTopic, "optionalMcq", level, e.target.value)}
                                                                  className="ms-4"
                                                                  style={{ width: "60px" }}
                                                                  disabled={!selectedMcqCoding[subTopic.sub_topic_id]?.optionalMcq || isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                                />
                                                                <span className="ms-2">/ {count?.mcq?.[level]}</span>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="row mt-2">
                                                        <div className="col-6">
                                                          <div className="me-4">
                                                            <input
                                                              type="checkbox"
                                                              checked={!!selectedMcqCoding[subTopic.sub_topic_id]?.coding}
                                                              className="me-2"
                                                              onChange={(e) => {
                                                                handleCheckboxChange(
                                                                  "coding",        // item (could be any identifier, using "coding" as placeholder)
                                                                  "coding",        // type
                                                                  subTopic,        // subtopic
                                                                  undefined,       // itemData (not needed for mcq/coding)
                                                                  e.target.checked // checked status
                                                                );
                                                              }}
                                                              disabled={isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                            />
                                                            <label>Coding</label>
                                                            <br />
                                                            {topicLevels.map(level => (
                                                              <div key={level}>
                                                                <label>{level.toLocaleUpperCase()} :</label>
                                                                <input
                                                                  type="number"
                                                                  min="0"
                                                                  max={count?.coding?.[level]}
                                                                  value={inputCounts[subTopic.sub_topic_id]?.coding?.[level] || 0}
                                                                  onChange={(e) => handleCountChange(subTopic, "coding", level, e.target.value)}
                                                                  className="ms-4"
                                                                  style={{ width: "60px" }}
                                                                  disabled={!selectedMcqCoding[subTopic.sub_topic_id]?.coding || isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                                />
                                                                <span className="ms-2">/ {count?.coding?.[level]}</span>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                        <div className="col-6">
                                                          <div className="me-4">
                                                            <input
                                                              type="checkbox"
                                                              checked={!!selectedMcqCoding[subTopic.sub_topic_id]?.optionalCoding}
                                                              className="me-2"
                                                              onChange={(e) => {
                                                                handleCheckboxChange(
                                                                  "optionalCoding", // item identifier
                                                                  "optionalCoding", // type
                                                                  subTopic,         // subtopic
                                                                  undefined,        // itemData not needed for optional coding
                                                                  e.target.checked  // checked status
                                                                );
                                                              }}
                                                              disabled={isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                            />
                                                            <label>Optional Coding</label>
                                                            <br />
                                                            {topicLevels.map(level => (
                                                              <div key={level}>
                                                                <label>{level.toLocaleUpperCase()} :</label>
                                                                <input
                                                                  type="number"
                                                                  min="0"
                                                                  max={count?.coding?.[level]}
                                                                  value={inputCounts[subTopic.sub_topic_id]?.optionalCoding?.[level] || 0}
                                                                  onChange={(e) => handleCountChange(subTopic, "optionalCoding", level, e.target.value)}
                                                                  className="ms-4"
                                                                  style={{ width: "60px" }}
                                                                  disabled={!selectedMcqCoding[subTopic.sub_topic_id]?.optionalCoding || isContentSelectionDisabled(subTopic.sub_topic_id)}
                                                                />
                                                                <span className="ms-2">/ {count?.coding?.[level]}</span>
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </Draggable>
                                          ))}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </DragDropContext>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>
      {showModal && (
        <Modal
          centered
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setNotebookPreviewModal(false);
          }}
          size={notebookPreviewModal ? "xl" : "lg"}
        >
          <Modal.Header closeButton>
            <Modal.Title>{notebookPreviewModal ? "Notebook preview" : "Preview"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className={notebookPreviewModal ? "p-0" : undefined}>
            {modalError ? (
              <div>{modalError}</div>
            ) : modalContent ? (
              modalContent
            ) : (
              <div>Loading...</div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {(fetchContentLoading || fetchSubjectsLoading || fetchCountLoading || fetchSubTopicLoading || fetchCourseLoading || fetchTopicLoading || fetchRestrictedLoading) && (
        <div className="d-flex justify-content-center align-items-center" style={{
          position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(255, 255, 255, 0.8)", zIndex: 9999 }}>
          <PulseLoader size={10} className="px-2" />
        </div>
      )}
    </div>
  );
};

export default CreateSubjectPlan;